import usePageTitle from "../utils/usePageTitle";
import React, { useEffect, useMemo, useState } from "react";
import { Button, Container, Select, TextAreaInput, TextInput } from "common-tailwind-components";
import { Formik, type FormikHelpers } from "formik";
import { FileJson, Save } from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  admin_create_recording,
  admin_get_recording_analysis_blob,
  admin_patch_recording,
  get_recording_details,
  get_users,
  splitApiRequestFormErrors,
  type AdminCreateRecordingRequest,
  type AdminPatchRecordingRequest,
} from "../api";
import type { RecordingDetails, User } from "../api/types";
import { useLogin } from "../reducers";
import { isAdmin } from "../utils/permissions";

type RecordingImportForm = {
  name: string;
  user_id: string;
  analysis_result: string;
  include_in_processes: boolean;
};

const emptyForm: RecordingImportForm = {
  name: "",
  user_id: "",
  analysis_result: "",
  include_in_processes: true,
};

/** The stored blob pretty-printed for the editor; empty when none is stored. */
const storedAnalysisJson = (stored: unknown): string =>
  stored == null ? "" : JSON.stringify(stored, null, 2);

const parseAnalysisJson = (raw: string): Record<string, never> => {
  const parsed = JSON.parse(raw);
  if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error("analysis_result must be a JSON object");
  }
  return parsed as Record<string, never>;
};

const RecordingImport = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { recordingId } = useParams<{ recordingId?: string }>();
  const { permissions } = useLogin();
  const editing = !!recordingId;
  const [users, setUsers] = useState<User[]>([]);
  const [recording, setRecording] = useState<RecordingDetails | null>(null);
  const [storedAnalysis, setStoredAnalysis] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitBusy, setSubmitBusy] = useState(false);

  usePageTitle(editing ? t("edit_recording_analysis") : t("import_recording_analysis"));

  const sortedUsers = useMemo(
    () => [...users].sort((a, b) => a.username.localeCompare(b.username)),
    [users],
  );
  const userLabels = useMemo(
    () => Object.fromEntries(users.map(user => [user.id, user.username])),
    [users],
  );

  useEffect(() => {
    if (!isAdmin(permissions)) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      get_users(),
      recordingId ? get_recording_details(recordingId) : Promise.resolve(null),
      // The editable value is the stored blob itself — analytics plus the
      // diagnostics report — not the customer-facing view of it.
      recordingId ? admin_get_recording_analysis_blob(recordingId) : Promise.resolve(null),
    ])
      .then(([users, recording, storedAnalysis]) => {
        if (cancelled) return;
        setUsers(users.items);
        setRecording(recording);
        setStoredAnalysis(storedAnalysis);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t("failed_to_load_recording_import_data"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [permissions, recordingId, t]);

  const initialValues: RecordingImportForm = useMemo(
    () =>
      recording
        ? {
            name: recording.name ?? "",
            user_id: recording.user_id,
            analysis_result: storedAnalysisJson(storedAnalysis),
            include_in_processes: recording.include_in_processes,
          }
        : {
            ...emptyForm,
            user_id: sortedUsers[0]?.id ?? "",
          },
    [recording, storedAnalysis, sortedUsers],
  );

  const onSubmit = async (
    values: RecordingImportForm,
    helpers: FormikHelpers<RecordingImportForm>,
  ) => {
    if (!isAdmin(permissions)) {
      setSubmitError(t("you_do_not_have_permission_to_import_recordings"));
      return;
    }
    setSubmitBusy(true);
    setSubmitError(null);
    helpers.setErrors({});
    try {
      let analysisResult: Record<string, never>;
      try {
        analysisResult = parseAnalysisJson(values.analysis_result);
      } catch (err: unknown) {
        helpers.setTouched({ analysis_result: true }, false);
        helpers.setErrors({
          analysis_result:
            err instanceof Error ? err.message : t("failed_to_save_recording_analysis"),
        });
        return;
      }
      if (editing && recordingId) {
        const body: AdminPatchRecordingRequest = {
          name: values.name.trim() || null,
          analysis_result: analysisResult,
        };
        const updated = await admin_patch_recording(recordingId, body);
        navigate(`/recordings/${encodeURIComponent(updated.id)}/`);
      } else {
        if (!values.user_id) {
          setSubmitError(t("select_user_for_recording_import"));
          return;
        }
        const body: AdminCreateRecordingRequest = {
          name: values.name.trim(),
          user_id: values.user_id,
          include_in_processes: values.include_in_processes,
          analysis_result: analysisResult,
        };
        const created = await admin_create_recording(body);
        navigate(`/recordings/${encodeURIComponent(created.id)}/`);
      }
    } catch (err: unknown) {
      const parsed = splitApiRequestFormErrors<RecordingImportForm>(err, t, [
        "name",
        "user_id",
        "analysis_result",
        "include_in_processes",
      ]);
      helpers.setTouched(parsed.touched, false);
      helpers.setErrors(parsed.fieldErrors);
      setSubmitError(parsed.remainder);
    } finally {
      setSubmitBusy(false);
    }
  };

  if (!isAdmin(permissions)) {
    return (
      <Container>
        <div className="mx-auto max-w-4xl px-4 py-8">
          <Link
            to="/administration/"
            className="inline-flex text-sm text-primary transition hover:text-primary/80"
          >
            {t("back_to_executive_dashboard")}
          </Link>
          <div className="mt-4 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            {t("you_do_not_have_permission_to_import_recordings")}
          </div>
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-8">
        <div className="space-y-2">
          <Link
            to="/administration/"
            className="inline-flex text-sm text-primary transition hover:text-primary/80"
          >
            {t("back_to_executive_dashboard")}
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-info/25 bg-info/10 text-info">
              <FileJson className="h-5 w-5" aria-hidden="true" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-text">
                {editing ? t("edit_recording_analysis") : t("import_recording_analysis")}
              </h1>
              <p className="text-sm text-text-dim">{t("recording_import_description")}</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
            {error}
          </div>
        )}

        {loading ? (
          <div className="rounded-xl border border-outline bg-surface px-4 py-3 text-sm text-text-dim">
            {t("loading_recording_import_data")}
          </div>
        ) : (
          <section className="rounded-xl border border-outline bg-surface p-5 shadow-sm">
            <Formik<RecordingImportForm>
              initialValues={initialValues}
              enableReinitialize
              onSubmit={onSubmit}
            >
              {({ handleSubmit, values, setFieldValue }) => (
                <form className="space-y-5" onSubmit={handleSubmit}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <TextInput
                      name="name"
                      label={t("recording_name")}
                      wrapped
                      disabled={submitBusy}
                    />
                    {editing ? (
                      <div className="space-y-1">
                        <span className="text-sm font-medium text-text">{t("user")}</span>
                        <div className="h-10 rounded-xl border border-outline bg-muted-surface px-3 py-2 text-sm text-text-dim">
                          {userLabels[values.user_id] ?? values.user_id}
                        </div>
                      </div>
                    ) : (
                      <Select
                        name="user_id"
                        label={t("user")}
                        choices={sortedUsers}
                        displayChoice={user => user.username}
                        required
                        readOnly={submitBusy || sortedUsers.length === 0}
                      />
                    )}
                  </div>

                  {!editing && (
                    <label className="inline-flex items-center gap-2 text-sm text-text">
                      <input
                        type="checkbox"
                        checked={values.include_in_processes}
                        onChange={event =>
                          setFieldValue("include_in_processes", event.target.checked)
                        }
                        disabled={submitBusy}
                        className="h-4 w-4 rounded border-outline accent-info"
                      />
                      {t("include_imported_recording_in_processes")}
                    </label>
                  )}

                  {editing && recording?.status.status !== "completed" && (
                    <div className="rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-strong">
                      {t("recording_import_no_existing_analysis")}
                    </div>
                  )}

                  <TextAreaInput
                    name="analysis_result"
                    label={t("analysis_result_json")}
                    helpText={t("analysis_result_json_help")}
                    rows={20}
                    wrapped
                  />

                  {submitError && (
                    <div className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
                      {submitError}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      color="primary"
                      type="submit"
                      disabled={submitBusy}
                      loading={submitBusy}
                      className="rounded-xl"
                    >
                      <Save className="h-4 w-4" aria-hidden="true" />
                      {editing ? t("save_recording_analysis") : t("import_recording_analysis")}
                    </Button>
                    {recordingId && (
                      <Link
                        to={`/recordings/${encodeURIComponent(recordingId)}/`}
                        className="rounded-xl border border-outline px-4 py-2 text-sm font-medium text-text transition hover:border-primary"
                      >
                        {t("cancel")}
                      </Link>
                    )}
                  </div>
                </form>
              )}
            </Formik>
          </section>
        )}
      </div>
    </Container>
  );
};

export default RecordingImport;
